import { store } from '@/store'
import { getRoute, optimizeRoute } from '.'
import { DB } from './database'
import { nativeDirections } from './directions'
import { LiveData, liveDataModel, LivePlaceTimes, RouteLiveState } from './models'
import { saveRoute } from './sync'
import { getFormattedCountdown, timestamp } from './tools'
import { getRouteLink } from '.'
import { RouteLogged } from './models/RouteLogged'

import { computeStats } from './stats'

export const getLiveData = (routeId: string): LiveData | null => {
  const liveData = store.live.getLiveData(routeId)
  return liveData ? { ...liveData } : null
}

export const logLiveRoute = async (routeId: string) => {
  const route = getRoute(routeId)
  const liveData = getLiveData(routeId)
  if (route && liveData) {
    const loggedRoute = RouteLogged(route, liveData)
    console.log(loggedRoute)
    return DB.createLoggedRoute(loggedRoute)
  }
}

export const goLive = async (routeId: string) => {
  if (!(store.user.isOwner(routeId) && store.user.permission('FEATURE_LIVE_MODE'))) {
    throw 'Live ruter er ikke en del af dit abonnement'
  }
  const route = getRoute(routeId)
  if (route.places.length < 2) {
    throw 'Live rute kræver minimum to stop'
  }
  route.live = true
  route.settings.detailed = true
  const detailedRoute = await optimizeRoute(route)
  const savedRoute = await saveRoute(detailedRoute)
  await DB.createLiveData(routeId, liveDataModel(route))
  return savedRoute
}

// Reset live mode while keeping driver secret
export const resetLive = async (routeId: string) => {
  const route = getRoute(routeId)
  const liveData = getLiveData(routeId)
  const resetLiveData = liveDataModel(route)
  if (liveData) resetLiveData.driverSecret = liveData.driverSecret
  return DB.updateLiveData(routeId, resetLiveData)
}

// End live mode
export const endLive = async (routeId: string) => {
  logLiveRoute(routeId)
  store.live.terminate(routeId)
  const route = getRoute(routeId)
  route.live = false
  route.settings.detailed = false
  const notDetailedRoute = await optimizeRoute(route)
  const savedRoute = await saveRoute(notDetailedRoute)
  await DB.deleteLiveData(routeId)
  return savedRoute
}

export const getPlaceAtIndex = (routeId: string, placeIndex: number) => {
  const route = getRoute(routeId)
  return route.places[placeIndex]
}

export const computeTimeOffset = (routeId: string, liveData: LiveData): number => {
  const places = getRoute(routeId).places
  return liveData.latestTime - (liveData.driving ?
    places[liveData.latestIndex].times.departure :
    places[liveData.latestIndex].times.arrival)
}

export const computeLiveState = (routeId: string, liveData: LiveData): RouteLiveState => {
  const route = getRoute(routeId)
  const places = route.places  
  const state: RouteLiveState = {
    driving: liveData.driving,
    latestIndex: liveData.latestIndex,
    latestTime: liveData.latestTime,
    timeOffset: computeTimeOffset(routeId, liveData),
    started: liveData.latestIndex > 0 || liveData.latestIndex == 0 && liveData.driving,
    finnished: liveData.latestIndex >= places.length - 1,
    current: places[liveData.latestIndex],
    next: places[liveData.latestIndex + 1] || null,
    places: places.map((place, i) => {
      return {
        ...place,
        live: {
          times: {
            arrival: liveData.places[i]?.arrival || place.times.arrival + computeTimeOffset(routeId, liveData),
            departure: liveData.places[i]?.departure || place.times.departure + computeTimeOffset(routeId, liveData)
          },
          latest: i === liveData.latestIndex && liveData.driving,
          current: i === liveData.latestIndex,
          visited: i < liveData.latestIndex || i <= liveData.latestIndex && liveData.driving,
          next: i === liveData.latestIndex + 1
        }
      }
    })
  }
  return state
}

export const getLiveState = (routeId: string): RouteLiveState | undefined => {
  const liveData = getLiveData(routeId)
  if (liveData) {
    return computeLiveState(routeId, liveData)
  }
}

export const onLiveUpdate = (routeId: string, cb: (liveData: LiveData) => void) => {
  return DB.onLiveData(routeId, liveData => {
    cb(liveData)
  })
}

export const nextPlace = async (routeId: string, navigate?: boolean) => {    
  const liveData = getLiveData(routeId)
  if (!liveData) return false
  
  const liveState = computeLiveState(routeId, liveData)  
  const places: LivePlaceTimes[] = [...liveData.places]

  if (liveState.driving) {
    places[liveState.latestIndex + 1].arrival = timestamp()
    DB.updateLiveData(routeId, {
      driving: false,
      latestIndex: liveState.latestIndex + 1,
      latestTime: timestamp(),
      places: places
    })  
  } else {
    places[liveState.latestIndex].departure = timestamp()
    DB.updateLiveData(routeId, {
      driving: true,
      latestTime: timestamp(),
      places: places
    })

    if (navigate) {
      nativeDirections(routeId, liveState.current, liveState.next)
    }
  }
}

export const prevPlace = async (routeId: string) => {    
  const liveData = getLiveData(routeId)
  if (!liveData) return false
  
  const liveState = computeLiveState(routeId, liveData)  
  const places: LivePlaceTimes[] = [...liveData.places]

  if (!liveState.driving) {
    places[liveState.latestIndex].arrival = 0
    DB.updateLiveData(routeId, {
      driving: true,
      latestIndex: liveState.latestIndex - 1,
      latestTime: places[liveState.latestIndex - 1].departure,
      places: places
    })  
  } else {
    places[liveState.latestIndex].departure = 0
    DB.updateLiveData(routeId, {
      driving: false,
      latestTime: places[liveState.latestIndex].arrival,
      places: places
    })
  }
}

export const getLiveProgress = async (liveState: RouteLiveState) => {  
  const timeToNext = (liveState.driving ? liveState.next.duration.to : liveState.current.times.departure - liveState.current.times.arrival)
  const timeSpent = timestamp() - liveState.latestTime
  const timeLeft = timeToNext - timeSpent
  const overtime = timeLeft < 0
  const hasBreak = liveState.current.times.departure - liveState.current.times.arrival > 0
  let ratio = timeSpent / timeToNext
  if (ratio > 1) ratio = 1  
  const clock = getFormattedCountdown(timeLeft)
  return { ratio, clock, overtime, hasBreak }
}


const getDriverSecret = (routeId: string) => {
  return store.live.getLiveData(routeId)?.driverSecret
}

export const getDriverLink = (routeId: string): string | null => {
  const driverSecret = getDriverSecret(routeId)
  if (driverSecret) {
    return getRouteLink(routeId) + '?d=' + driverSecret
  }
  return null
}

export const validateDriverSecret = (routeId: string, aDriverSecret: string): boolean => {  
  const driverSecret = getDriverSecret(routeId)
  const isValid = !!driverSecret && driverSecret === aDriverSecret
  !isValid && store.modal.showModal('Message', {title: 'Ugyldigt chauffør-link', content: 'Det chauffør-link du har prøvet at åbne er ikke længere gyldigt. Bed personen om, at sende linket igen.'})
  return isValid
}



// IF USER is DRIVER or WANTS TO BE DRIVER
/*export const startTracking = (routeId: string) => {
  
  if (navigator.geolocation) {
    // Get location every 5 seconds
    const options = {
      enableHighAccuracy: false,
      timeout: 5000,
      maximumAge: 0
    }
    return navigator.geolocation.watchPosition((pos) => {
      liveDataRef(routeId).update({
        'driver.position': new CoordinatesModel(pos.coords.latitude, pos.coords.longitude),
        'driver.positionTime': pos.timestamp
      })
    }, (err) => {
      toast({title: 'Error', msg: err.message})
      // if (err.code === 1) console.error('OBS', ' Tillad deling af placering, for at udnytte alle funktioner i LIVE-mode')
      // else if (err.code === 3) console.error('Fejl', ' Det var ikke muligt, at hente din placering.')
      // else console.error('Fejl', err.message)
    }, options)
  } else {
    toast({title: 'Error', msg: 'Din browser undestøtter ikke deling af placering.'})
  }
}*/

export const stopTracking = (watchId: number) => {
  navigator.geolocation.clearWatch(watchId)
}

export const assignDriver = (routeId: string, uid: string) => {
  // routeRef(routeId).update({'roles.driver': uid})
}

